// @ts-nocheck
import { Check, Pencil, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { AI_AGENT_FIELD_LIMITS } from '@/utilities/constant';
import { getAiAgentDescriptionCharLimit } from '@/utilities/aiAgentAcl';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import CollapsibleSection from './CollapsibleSection';
import HeadingInfo from './HeadingInfo';

const {
  DATA_VARIABLE_GROUPS_MAX_ITEMS,
  DATA_VARIABLES_PER_GROUP_MAX_ITEMS,
  DATA_VARIABLE_GROUP_NAME_MAX,
  DATA_VARIABLE_KEY_MAX,
  DATA_VARIABLE_DEFAULT_MAX,
} = AI_AGENT_FIELD_LIMITS;


export const VARIABLE_TYPES = ['string', 'number', 'boolean', 'date'];
export const VARIABLE_SOURCES = ['collected', 'provided', 'api'];

const GROUP_NAME_RE = /^[A-Za-z0-9_]+$/; // letters, numbers, underscores
const KEY_RE = /^[a-z0-9_]+$/; // snake_case


export const normalizeVariableType = (t: string) => {
  const v = String(t || '').toLowerCase();
  if (VARIABLE_TYPES.includes(v)) return v;
  if (v === 'integer') return 'number';
  return 'string';
};
export const normalizeVariableSource = (s: string) => {
  const v = String(s || '').toLowerCase();
  if (VARIABLE_SOURCES.includes(v)) return v;
  if (v === 'runtime') return 'api';
  if (v === 'static') return 'provided';
  return 'collected';
};

const makeVariable = () => ({
  id: crypto.randomUUID(),
  key: '',
  type: 'string',
  source: 'collected',
  default: '',
  required: false,
  description: '',
});

const makeGroup = () => ({
  id: crypto.randomUUID(),
  variable_name: '',
  variable_description: '',
  variables: [makeVariable()],
});

export function coerceDefault(raw: string, type: string) {
  if (raw === '' || raw === undefined || raw === null) return null;
  const s = String(raw);
  if (type === 'boolean') return s.trim().toLowerCase() === 'true';
  if (type === 'number') {
    const n = Number(s);
    return s.trim() === '' || Number.isNaN(n) ? null : n;
  }
  return s; 
}

export function buildDataVariablesGroups(groups: any[]) {
  return (groups || [])
    .filter((g) => g.variable_name)
    .map((g) => ({
      variable_name: g.variable_name,
      variable_description: g.variable_description || '',
      variables: g.variables
        .filter((v: any) => v.key)
        .map((v: any) => {
          const type = normalizeVariableType(v.type);
          return {
            key: v.key,
            type,
            source: normalizeVariableSource(v.source),
            default: coerceDefault(v.default, type),
            required: Boolean(v.required),
            description: v.description || '',
          };
        }),
    }));
}


const errClass = (hasErr: boolean) =>
  hasErr ? 'border-destructive focus-visible:ring-destructive' : '';


const clip = (value: string, max: number, id: string, label: string) => {
  if (value.length > max) {
    toast.error(`${label} can be max ${max} characters`, { id });
    return value.slice(0, max);
  }
  return value;
};


const FieldMeta = ({ error, count, max }: { error?: string; count: number; max: number }) => (
  <div className="flex items-start justify-between gap-2">
    <p className="text-xs text-destructive">
      {error || (count >= max ? `Max ${max} characters reached` : '')}
    </p>
    <span className="shrink-0 text-xs text-muted-foreground">
      {count}/{max}
    </span>
  </div>
);

const getDefaultError = (value: any, type: string) => {
  const s = String(value ?? '');
  if (s === '') return '';
  if (type === 'boolean' && !['true', 'false'].includes(s.trim().toLowerCase())) {
    return 'Default must be true or false for a boolean variable';
  }
  if (type === 'number' && (s.trim() === '' || Number.isNaN(Number(s)))) {
    return 'Default must be a valid number for a number variable';
  }
  return '';
};

export default function DataVariablesSection({ value, onChange }: any) {
  const groups = value || [];
  const descLimit = getAiAgentDescriptionCharLimit();

  const [editingGroupId, setEditingGroupId] = useState<string | null>(null);
  const [openVariableId, setOpenVariableId] = useState<Record<string, string | null>>({});
  const [attempted, setAttempted] = useState<Record<string, boolean>>({});
  const markAttempted = (...ids: string[]) =>
    setAttempted((prev) => ({ ...prev, ...Object.fromEntries(ids.map((i) => [i, true])) }));

  const INFO = {
    groupName: 'The name of this group of related variables. Example: caller_data.',
    groupDesc: 'What this group of variables is for.',
    key: 'The unique name of this variable, used to reference it in prompts and API calls. Example: caller_name.',
    type: 'The kind of value this variable holds.',
    source:
      'Where the value comes from. Collected is asked from the caller. Provided is passed in before the call. API is filled from an API response.',
    default: 'A fallback value used when no value is collected. Optional.',
    required: 'When on, the agent must get this value before moving on in the call.',
    description: "Explains what this variable is and how it's used, so the agent knows what to ask for.",
  };

  // ---------- Validation ----------
  const getGroupErrors = (group: any, showRequired: boolean) => {
    const e: any = {};
    const n = (group.variable_name || '').trim();
    if (!n) {
      if (showRequired) e.name = 'Group name is required';
    } else if (!GROUP_NAME_RE.test(n)) {
      e.name = 'Only letters, numbers and underscores are allowed';
    } else if (n.length > DATA_VARIABLE_GROUP_NAME_MAX) {
      e.name = `Group name can be max ${DATA_VARIABLE_GROUP_NAME_MAX} characters`;
    }
    if ((group.variable_description || '').length > descLimit) {
      e.description = `Group description can be max ${descLimit} characters`;
    }
    return e;
  };

  const getVariableErrors = (variable: any, group: any, showRequired: boolean) => {
    const e: any = {};
    const k = (variable.key || '').trim();
    if (!k) {
      if (showRequired) e.key = 'Variable key is required';
    } else if (!KEY_RE.test(k)) {
      e.key = 'Key must be snake_case (lowercase letters, numbers, underscores)';
    } else if (k.length > DATA_VARIABLE_KEY_MAX) {
      e.key = `Key can be max ${DATA_VARIABLE_KEY_MAX} characters`;
    } else if (group.variables.some((o: any) => o.id !== variable.id && (o.key || '').trim() === k)) {
      e.key = 'Key must be unique within this group';
    }

    const defErr = getDefaultError(variable.default, variable.type);
    if (defErr) e.default = defErr;
    else if ((variable.default || '').length > DATA_VARIABLE_DEFAULT_MAX) {
      e.default = `Default can be max ${DATA_VARIABLE_DEFAULT_MAX} characters`;
    }

    if ((variable.description || '').length > descLimit) {
      e.description = `Description can be max ${descLimit} characters`;
    }
    return e;
  };

  const firstError = (errs: any) => errs.key || errs.name || errs.default || errs.description;

  // ---------- Groups ----------
  const addGroup = () => {
    if (groups.length >= DATA_VARIABLE_GROUPS_MAX_ITEMS) {
      toast.error(`You can add up to ${DATA_VARIABLE_GROUPS_MAX_ITEMS} groups per agent`);
      return;
    }
    const current = groups.find((g: any) => g.id === editingGroupId);
    if (current && !current.variable_name?.trim()) {
      markAttempted(current.id);
      toast.error('Fill the current group name and click the tick first');
      return;
    }
    const next = makeGroup();
    onChange([...groups, next]);
    setEditingGroupId(next.id);
    setOpenVariableId((prev) => ({ ...prev, [next.id]: next.variables[0].id }));
  };

  const removeGroup = (id: string) => {
    onChange(groups.filter((g: any) => g.id !== id));
    if (editingGroupId === id) setEditingGroupId(null);
  };

  const updateGroup = (id: string, patch: any) => {
    onChange(groups.map((g: any) => (g.id === id ? { ...g, ...patch } : g)));
  };

  const openEditGroup = (id: string) => {
    setEditingGroupId(id);
    setOpenVariableId((prev) => ({ ...prev, [id]: null }));
  };

 
  const handleGroupNameChange = (group: any, raw: string) => {
    const spaced = raw.replace(/\s+/g, '_');
    const cleaned = spaced.replace(/[^A-Za-z0-9_]/g, '');
    if (cleaned !== spaced) {
      toast.error('Group name can have only letters, numbers and underscores', { id: 'dv-group-chars' });
    }
    updateGroup(group.id, {
      variable_name: clip(cleaned, DATA_VARIABLE_GROUP_NAME_MAX, 'dv-group-max', 'Group name'),
    });
  };

  // Tick on a group: validate, drop blank leftover variables, move it to the listing.
  const commitGroup = (group: any) => {
    const gErrs = getGroupErrors(group, true);
    if (Object.keys(gErrs).length) {
      markAttempted(group.id);
      toast.error(firstError(gErrs));
      return;
    }
    const keyed = group.variables.filter((v: any) => v.key?.trim());
    if (keyed.length === 0) {
      toast.error('Add at least one variable with a key');
      return;
    }

    const trimmedGroup = { ...group, variables: keyed };
    for (const v of keyed) {
      const vErrs = getVariableErrors(v, trimmedGroup, true);
      if (Object.keys(vErrs).length) {
        markAttempted(v.id);
        setOpenVariableId((prev) => ({ ...prev, [group.id]: v.id }));
        toast.error(`Variable "${v.key}": ${firstError(vErrs)}`);
        return;
      }
    }
    if (keyed.length !== group.variables.length) {
      updateGroup(group.id, { variables: keyed });
    }
    setOpenVariableId((prev) => ({ ...prev, [group.id]: null }));
    setEditingGroupId(null);
  };

  // ---------- Variables ----------
  const addVariable = (groupId: string) => {
    const group = groups.find((g: any) => g.id === groupId);
    if (group && group.variables.length >= DATA_VARIABLES_PER_GROUP_MAX_ITEMS) {
      toast.error(`You can add up to ${DATA_VARIABLES_PER_GROUP_MAX_ITEMS} variables per group`);
      return;
    }
    const openVar = group?.variables.find((v: any) => v.id === openVariableId[groupId]);
    if (openVar && !openVar.key?.trim()) {
      markAttempted(openVar.id);
      toast.error('Fill the current variable key and click the tick first');
      return;
    }
    const item = makeVariable();
    onChange(
      groups.map((g: any) =>
        g.id === groupId ? { ...g, variables: [...g.variables, item] } : g,
      ),
    );
    setOpenVariableId((prev) => ({ ...prev, [groupId]: item.id }));
  };

  const updateVariable = (groupId: string, variableId: string, patch: any) => {
    onChange(
      groups.map((g: any) =>
        g.id === groupId
          ? {
              ...g,
              variables: g.variables.map((v: any) => (v.id === variableId ? { ...v, ...patch } : v)),
            }
          : g,
      ),
    );
  };

  const removeVariable = (groupId: string, variableId: string) => {
    onChange(
      groups.map((g: any) =>
        g.id === groupId
          ? { ...g, variables: g.variables.filter((v: any) => v.id !== variableId) }
          : g,
      ),
    );
    setOpenVariableId((prev) => (prev[groupId] === variableId ? { ...prev, [groupId]: null } : prev));
  };

  const openEditVariable = (groupId: string, variableId: string) => {
    setOpenVariableId((prev) => ({ ...prev, [groupId]: variableId }));
  };

 
  const handleKeyChange = (groupId: string, variableId: string, raw: string) => {
    const spaced = raw.toLowerCase().replace(/\s+/g, '_');
    const cleaned = spaced.replace(/[^a-z0-9_]/g, '');
    if (cleaned !== spaced) {
      toast.error('Key must be snake_case (lowercase letters, numbers and underscores only)', {
        id: 'dv-key-chars',
      });
    }
    updateVariable(groupId, variableId, {
      key: clip(cleaned, DATA_VARIABLE_KEY_MAX, 'dv-key-max', 'Variable key'),
    });
  };


  const commitVariable = (groupId: string, variable: any) => {
    const group = groups.find((g: any) => g.id === groupId);
    const errs = getVariableErrors(variable, group, true);
    if (Object.keys(errs).length) {
      markAttempted(variable.id);
      toast.error(firstError(errs));
      return;
    }
    setOpenVariableId((prev) => ({ ...prev, [groupId]: null }));
  };

  return (
    <CollapsibleSection title="Data Variables">
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <p className="text-sm font-semibold text-foreground">
            Data Variables
            <span className="ml-1.5 text-xs font-normal text-muted-foreground">
              ({groups.length}/{DATA_VARIABLE_GROUPS_MAX_ITEMS} groups)
            </span>
          </p>
          <Button
            size="sm"
            variant="outline"
            onClick={addGroup}
            disabled={groups.length >= DATA_VARIABLE_GROUPS_MAX_ITEMS}
          >
            <Plus className="h-4 w-4" />
            Add Group
          </Button>
        </div>

        {groups.length === 0 && (
          <p className="py-4 text-center text-sm text-muted-foreground">
            No data variables added yet.
          </p>
        )}

        {groups.map((group: any, index: number) => {
          // ---------- Listing row (group already ticked) ----------
          if (group.id !== editingGroupId) {
            const count = group.variables.filter((v: any) => v.key?.trim()).length;
            return (
              <div
                key={group.id}
                className="flex items-center justify-between rounded-md border px-3 py-2"
              >
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-foreground">
                    {group.variable_name || `Group ${index + 1}`}
                    <span className="ml-1.5 text-xs font-normal text-muted-foreground">
                      ({count} {count === 1 ? 'variable' : 'variables'})
                    </span>
                  </p>
                  {group.variable_description && (
                    <p className="truncate text-xs text-muted-foreground">
                      {group.variable_description}
                    </p>
                  )}
                </div>
                <span className="flex shrink-0 items-center gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-primary hover:text-primary"
                    onClick={() => openEditGroup(group.id)}
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-destructive hover:text-destructive"
                    onClick={() => removeGroup(group.id)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </span>
              </div>
            );
          }

          const gErrs = getGroupErrors(group, !!attempted[group.id]);
          const nameLen = (group.variable_name || '').length;
          const gDescLen = (group.variable_description || '').length;

          return (
            <div key={group.id} className="rounded-md border border-border">
              <div className="flex items-start gap-2 p-3">
                <div className="grid min-w-0 flex-1 grid-cols-1 gap-3 sm:grid-cols-2">
                  <div className="space-y-1.5">
                    <div className="flex items-center gap-1.5">
                      <Label className="text-xs">
                        Group {index + 1} Name <span className="text-destructive">*</span>
                      </Label>
                      <HeadingInfo text={INFO.groupName} />
                    </div>
                    <Input
                      placeholder="e.g. guest_data"
                      value={group.variable_name}
                      onChange={(e) => handleGroupNameChange(group, e.target.value)}
                      className={errClass(!!gErrs.name || nameLen >= DATA_VARIABLE_GROUP_NAME_MAX)}
                    />
                    <FieldMeta error={gErrs.name} count={nameLen} max={DATA_VARIABLE_GROUP_NAME_MAX} />
                  </div>
                  <div className="space-y-1.5">
                    <div className="flex items-center gap-1.5">
                      <Label className="text-xs">Group Description</Label>
                      <HeadingInfo text={INFO.groupDesc} />
                    </div>
                    <Input
                      placeholder="e.g. Identity and contact details about the caller"
                      value={group.variable_description}
                      onChange={(e) =>
                        updateGroup(group.id, {
                          variable_description: clip(
                            e.target.value,
                            descLimit,
                            'dv-group-desc-max',
                            'Group description',
                          ),
                        })
                      }
                      className={errClass(!!gErrs.description || gDescLen >= descLimit)}
                    />
                    <FieldMeta error={gErrs.description} count={gDescLen} max={descLimit} />
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="mt-5 h-7 w-7 shrink-0 text-emerald-600 hover:text-emerald-700"
                  onClick={() => commitGroup(group)}
                  title="Done"
                  aria-label="Done"
                >
                  <Check className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="mt-5 h-7 w-7 shrink-0 text-destructive hover:text-destructive"
                  onClick={() => removeGroup(group.id)}
                  title="Delete group"
                  aria-label="Delete group"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>

              <div className="space-y-3 border-t border-border p-3">
                <div className="flex items-center justify-between">
                  <p className="text-xs font-semibold text-foreground">
                    Variables
                    <span className="ml-1.5 font-normal text-muted-foreground">
                      ({group.variables.length}/{DATA_VARIABLES_PER_GROUP_MAX_ITEMS})
                    </span>
                  </p>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => addVariable(group.id)}
                    disabled={group.variables.length >= DATA_VARIABLES_PER_GROUP_MAX_ITEMS}
                  >
                    <Plus className="h-3.5 w-3.5" />
                    Add Variable
                  </Button>
                </div>

                {group.variables.map((variable: any, vIndex: number) => {
                  if (variable.id !== openVariableId[group.id]) {
                    return (
                      <div
                        key={variable.id}
                        className="flex items-center justify-between rounded-md border px-3 py-2"
                      >
                        <div className="min-w-0">
                          <p className="truncate text-sm font-medium text-foreground">
                            {variable.key || `Variable ${vIndex + 1}`}
                            <span className="ml-1.5 text-xs font-normal text-muted-foreground">
                              ({variable.type})
                            </span>
                          </p>
                          {variable.description && (
                            <p className="truncate text-xs text-muted-foreground">
                              {variable.description}
                            </p>
                          )}
                        </div>
                        <span className="flex shrink-0 items-center gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-primary hover:text-primary"
                            onClick={() => openEditVariable(group.id, variable.id)}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-destructive hover:text-destructive"
                            onClick={() => removeVariable(group.id, variable.id)}
                            disabled={group.variables.length === 1}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </span>
                      </div>
                    );
                  }

                  const vErrs = getVariableErrors(variable, group, !!attempted[variable.id]);
                  const keyLen = (variable.key || '').length;
                  const defLen = String(variable.default ?? '').length;
                  const descLen = (variable.description || '').length;

                  return (
                    <div
                      key={variable.id}
                      className="space-y-3 rounded-md border border-border bg-muted/30 p-3"
                    >
                      <div className="flex items-center justify-between">
                        <p className="text-xs font-semibold text-foreground">Variable {vIndex + 1}</p>
                        <span className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6 text-emerald-600 hover:text-emerald-700"
                            onClick={() => commitVariable(group.id, variable)}
                            title="Done"
                            aria-label="Done"
                          >
                            <Check className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6 text-destructive hover:text-destructive"
                            onClick={() => removeVariable(group.id, variable.id)}
                            disabled={group.variables.length === 1}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </span>
                      </div>

                      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                        <div className="space-y-1.5">
                          <div className="flex items-center gap-1.5">
                            <Label>
                              Key <span className="text-destructive">*</span>
                            </Label>
                            <HeadingInfo text={INFO.key} />
                          </div>
                          <Input
                            placeholder="e.g. guest_name"
                            value={variable.key}
                            onChange={(e) => handleKeyChange(group.id, variable.id, e.target.value)}
                            className={errClass(!!vErrs.key || keyLen >= DATA_VARIABLE_KEY_MAX)}
                          />
                          <FieldMeta error={vErrs.key} count={keyLen} max={DATA_VARIABLE_KEY_MAX} />
                        </div>

                        <div className="space-y-1.5">
                          <div className="flex items-center gap-1.5">
                            <Label>Type</Label>
                            <HeadingInfo text={INFO.type} />
                          </div>
                          <Select
                            value={variable.type}
                            onValueChange={(val) =>
                              updateVariable(group.id, variable.id, { type: val })
                            }
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select type" />
                            </SelectTrigger>
                            <SelectContent>
                              {VARIABLE_TYPES.map((t) => (
                                <SelectItem key={t} value={t}>
                                  {t}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>

                        <div className="space-y-1.5">
                          <div className="flex items-center gap-1.5">
                            <Label>Source</Label>
                            <HeadingInfo text={INFO.source} />
                          </div>
                          <Select
                            value={variable.source}
                            onValueChange={(val) =>
                              updateVariable(group.id, variable.id, { source: val })
                            }
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select source" />
                            </SelectTrigger>
                            <SelectContent>
                              {VARIABLE_SOURCES.map((s) => (
                                <SelectItem key={s} value={s}>
                                  {s}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>

                        <div className="space-y-1.5">
                          <div className="flex items-center gap-1.5">
                            <Label>Default</Label>
                            <HeadingInfo text={INFO.default} />
                          </div>
                          <Input
                            placeholder={variable.type === 'boolean' ? 'true / false' : 'Leave blank for null'}
                            value={variable.default}
                            onChange={(e) =>
                              updateVariable(group.id, variable.id, {
                                default: clip(
                                  e.target.value,
                                  DATA_VARIABLE_DEFAULT_MAX,
                                  'dv-default-max',
                                  'Default value',
                                ),
                              })
                            }
                            className={errClass(!!vErrs.default || defLen >= DATA_VARIABLE_DEFAULT_MAX)}
                          />
                          <FieldMeta
                            error={vErrs.default}
                            count={defLen}
                            max={DATA_VARIABLE_DEFAULT_MAX}
                          />
                        </div>
                      </div>

                      <div className="space-y-1.5">
                        <div className="flex items-center gap-1.5">
                          <Label>Description</Label>
                          <HeadingInfo text={INFO.description} />
                        </div>
                        <Textarea
                          rows={2}
                          placeholder="e.g. The caller's full name. Used to create or match a guest record."
                          value={variable.description}
                          onChange={(e) =>
                            updateVariable(group.id, variable.id, {
                              description: clip(
                                e.target.value,
                                descLimit,
                                'dv-desc-max',
                                'Variable description',
                              ),
                            })
                          }
                          className={errClass(!!vErrs.description || descLen >= descLimit)}
                        />
                        <FieldMeta error={vErrs.description} count={descLen} max={descLimit} />
                      </div>

                      <div className="flex items-center gap-2">
                        <Checkbox
                          id={`required-${variable.id}`}
                          checked={variable.required}
                          onCheckedChange={(checked) =>
                            updateVariable(group.id, variable.id, { required: checked === true })
                          }
                        />
                        <Label htmlFor={`required-${variable.id}`} className="cursor-pointer text-sm">
                          Required
                        </Label>
                        <HeadingInfo text={INFO.required} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </CollapsibleSection>
  );
}
